// April 2026 monthly report document.
import { defineDocument, em, link, list, p, section, strong, table, text, } from '../../schema';
export const report202604TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-04-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-04-top-party-games-and-streamers',
    route: '/party-central/reports/2026-04-top-party-games-and-streamers',
    seo: {
        title: 'April 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for April 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'April 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'sky',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-05-02T00:00:00.000Z'),
        updatedAt: new Date('2026-05-10T00:00:00.000Z'),
        reportMonth: '2026-04',
        dataRange: {
            start: '2026-04-01',
            end: '2026-04-30',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('In April we tracked 189,539 party game streams from 79,704 streamers across 989 games and 35 languages. See '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text(" for collection details. There was a slight downtick in the number of streams and streamers compared to March that isn't fully explained by the shorter month. However, the variety of party games played increased month-over-month, with attention drawn to new releases such as "),
                    em('Flock Around'),
                    text(', a multiplayer bird watching game.'),
                ]),
                p([
                    text('Off the back of its latest update, '),
                    em('Lethal Company'),
                    text(' took over as the most streamed game from '),
                    em('Diablo II: Resurrected'),
                    text(' but '),
                    em('nanos world'),
                    text(' received the top Party Score because it paired meaningful audience draw with high repeat play. '),
                    em('Words On Stream'),
                    text(' also stayed near the top of the Party Score rankings because hundreds of streamers kept returning to it throughout the month.'),
                ]),
                p([
                    link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY'),
                    text(" grabbed the top Party Score for streamers because he played several party games and streamed regularly with high average viewership. Four other creators shared a 98 Party Score in April and while they streamed more consistently, they didn't have the viewership or variety to grab the top spot."),
                ]),
                p('While there was a global drop in stream count from March, the number of Spanish and French streams notably increased. French made the largest move with stream count up 13% month-over-month, enough to surpass German in the rankings and take the #3 spot.'),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            blocks: [
                p([
                    text('Party Score is a proprietary 0-100 composite that ranks party-game performance on Twitch. '),
                    strong('Game Party Score'),
                    text(' combines streamer breadth, stickiness, and audience draw, so it can elevate titles with committed communities even when they are not the biggest by raw volume. '),
                    strong('Streamer Party Score'),
                    text(' applies the same idea to creators, weighting consistency, audience size, and game variety. Full scoring details are in the '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text('.'),
                ]),
            ],
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            text("This month's biggest surprise was also the number one Party Game in the rankings, "),
                            em('nanos world'),
                            text('. The game saw only 157 streams in March but jumped to 2,127 streams in April, a 14x increase! The surge was led almost entirely by the French community and their dedicated play of '),
                            em('Zenkai'),
                            text(', a new ninja role-playing game from the developer Genius Game, built on top of the '),
                            em('nanos world'),
                            text(' platform.'),
                        ]),
                        p([
                            text('Other notable movements in the Party Score rankings include '),
                            em('Mario Kart'),
                            text(' with two titles moving into the top five and '),
                            em('Words On Stream'),
                            text(' moving up to the number 2 spot on stable repeat play. Despite being the third most-streamed game of the month, '),
                            em('Fall Guys'),
                            text(' completely fell out of the top 15 with viewership down 62% from last month. '),
                            em('Diablo II: Resurrected'),
                            text(' also continued its sharp downward trend with the number of unique streamers dropping 50% from last month and its typically excellent stickiness starting to show cracks as well.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'nanos world', '96', '2,127', '201', '10.6', '65'],
                                ['2', 'Words On Stream', '93', '7,215', '648', '11.1', '17'],
                                [
                                    '3',
                                    'Mario Kart World',
                                    '92',
                                    '6,038',
                                    '2,472',
                                    '2.4',
                                    '25',
                                ],
                                [
                                    '4',
                                    'Diablo II: Resurrected',
                                    '92',
                                    '8,983',
                                    '1,568',
                                    '5.7',
                                    '16',
                                ],
                                ['5', 'Mario Kart 64', '92', '326', '118', '2.8', '55'],
                                [
                                    '6',
                                    'Super Smash Bros. Ultimate',
                                    '91',
                                    '12,666',
                                    '3,761',
                                    '3.4',
                                    '15',
                                ],
                                ['7', 'Mahjong Soul', '91', '4,229', '1,611', '2.6', '18'],
                                [
                                    '8',
                                    'Placid Plastic Duck Simulator',
                                    '91',
                                    '1,827',
                                    '311',
                                    '5.9',
                                    '14',
                                ],
                                [
                                    '9',
                                    'Just Dance 2026 Edition',
                                    '91',
                                    '495',
                                    '214',
                                    '2.3',
                                    '42',
                                ],
                                ['10', 'Pokémon Stadium', '91', '219', '98', '2.2', '87'],
                                ['11', 'Gartic on Stream', '91', '459', '42', '10.9', '43'],
                                ['12', 'Among Us', '90', '14,042', '8,344', '1.7', '41'],
                                [
                                    '13',
                                    'Jackbox Party Packs',
                                    '90',
                                    '4,211',
                                    '2,012',
                                    '2.1',
                                    '24',
                                ],
                                [
                                    '14',
                                    'Super Mario Party Jamboree',
                                    '90',
                                    '1,436',
                                    '788',
                                    '1.8',
                                    '58',
                                ],
                                ['15', 'Miitopia', '90', '201', '94', '2.1', '75'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY'),
                            text(' led all streamers in Party Score for April. Compared to March, we captured him playing party games almost twice as often, with a 20% increase in viewership and a more diverse set of party games played. '),
                            link('https://twitch.tv/buni', 'Buni'),
                            text(', '),
                            link('https://twitch.tv/sliverrk', 'SliverRK'),
                            text(', and '),
                            link('https://twitch.tv/nohoho12', 'のほほ'),
                            text(" achieved the same Party Score, primarily through streaming a few games consistently. While their average viewership is impressive, it wasn't enough to steal the top spot this month."),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY')],
                                    '98',
                                    '14',
                                    '1,264',
                                    '10',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/buni', 'Buni')],
                                    '98',
                                    '40',
                                    '365',
                                    '3',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/sliverrk', 'SliverRK')],
                                    '98',
                                    '74',
                                    '258',
                                    '3',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '98',
                                    '36',
                                    '240',
                                    '3',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '98',
                                    '26',
                                    '156',
                                    '7',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/ponce', 'Ponce')],
                                    '97',
                                    '10',
                                    '3,730',
                                    '4',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/piapiufo', 'PiaPiUFO')],
                                    '97',
                                    '12',
                                    '556',
                                    '4',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/karacorvus', 'KaraCorvus')],
                                    '97',
                                    '13',
                                    '331',
                                    '5',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '97',
                                    '16',
                                    '206',
                                    '12',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/ttpprrcod', 'ttpprrcod')],
                                    '97',
                                    '36',
                                    '97',
                                    '3',
                                ],
                                [
                                    '11',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '97',
                                    '33',
                                    '86',
                                    '8',
                                ],
                                [
                                    '12',
                                    [link('https://twitch.tv/ray', 'Ray')],
                                    '96',
                                    '9',
                                    '2,897',
                                    '5',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/antilipsi', 'ANTILIPSI')],
                                    '96',
                                    '21',
                                    '1,199',
                                    '2',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/zeroyalviking', 'ZeRoyalViking')],
                                    '96',
                                    '11',
                                    '377',
                                    '6',
                                ],
                                [
                                    '15',
                                    [link('https://twitch.tv/emilycc', 'Emilycc')],
                                    '96',
                                    '100',
                                    '232',
                                    '2',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            blocks: [
                p('We split the global state of party games into four different leaderboards to get a stronger sense of which games streamers and their audiences connect the most with.'),
            ],
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Lethal Company'),
                            text(' was April\'s clear winner. Players flocked to the long-awaited release of Lethal Company Version 80, known as "The Blooming Update," which introduced significant new content after a long hiatus. Key additions include three new creatures, a "March" moon rework, new factory interior rooms, and a new utility slot, improving both content variety and technical performance. The rest of the most-streamed games were relatively steady with '),
                            em('Among Us'),
                            text(', '),
                            em('Fall Guys'),
                            text(', '),
                            em('Super Battle Golf'),
                            text(', and '),
                            em('Super Smash Bros. Ultimate'),
                            text(' remaining the foundation of party game activity on Twitch, even as overall stream volume cooled.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'Lethal Company', '14,550', '8,680', '21'],
                                ['2', 'Among Us', '14,042', '8,344', '41'],
                                ['3', 'Fall Guys', '13,020', '6,271', '18'],
                                ['4', 'Super Battle Golf', '12,799', '8,913', '26'],
                                ['5', 'Super Smash Bros. Ultimate', '12,666', '3,761', '15'],
                                ['6', 'Diablo II: Resurrected', '8,983', '1,568', '16'],
                                ['7', 'Words On Stream', '7,215', '648', '17'],
                                ['8', 'Mario Kart World', '6,038', '2,472', '25'],
                                ['9', 'Gartic Phone', '4,964', '2,782', '17'],
                                ['10', 'Mahjong Soul', '4,229', '1,611', '18'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('8 Ball Pool'),
                            text(' reached #1 because of one very large stream from '),
                            link('https://twitch.tv/caseoh_', 'caseoh_'),
                            text(', not because billiards games suddenly had broad appeal with Twitch viewers. The same thing happened with '),
                            em('8POOL'),
                            text(': caseoh_ drove a significant audience spike and '),
                            link('https://twitch.tv/itswill', 'itswill'),
                            text(' added one too. The April numbers are better understood as a creator-driven moment rather than a category-wide pool trend. '),
                            em('Maitre des Fleurs'),
                            text(' and '),
                            em('Casino'),
                            text(' have both shown staying power as Games with Big Audiences, now making the list for three months in a row.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                ['1', '8 Ball Pool', '3,434', '16', '12'],
                                ['2', 'Jinsei Game for Nintendo Switch', '1,102', '16', '10'],
                                ['3', '8POOL', '457', '111', '44'],
                                ['4', 'Maitre des Fleurs', '441', '33', '20'],
                                ['5', 'Casino', '338', '94', '47'],
                                ['6', 'Pixoguess', '298', '16', '13'],
                                ['7', 'West Hunt', '278', '28', '27'],
                                [
                                    '8',
                                    'Clubhouse Games: 51 Worldwide Classics',
                                    '255',
                                    '70',
                                    '55',
                                ],
                                ['9', 'LEGO Rock Band', '219', '16', '4'],
                                ['10', 'Make Way', '212', '87', '75'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Super Battle Golf'),
                            text(' still reached the most streamers this month, even though its numbers dipped slightly from last month. However, the biggest story was the massive hype around '),
                            em("Lethal Company's"),
                            text(' latest release, which drove an astonishing 94% month-over-month increase in unique streamers.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'Super Battle Golf', '8,913', '12,799'],
                                ['2', 'Lethal Company', '8,680', '14,550'],
                                ['3', 'Among Us', '8,344', '14,042'],
                                ['4', 'Fall Guys', '6,271', '13,020'],
                                ['5', 'Super Smash Bros. Ultimate', '3,761', '12,666'],
                                ['6', 'Gartic Phone', '2,782', '4,964'],
                                ['7', 'Mario Kart World', '2,472', '6,038'],
                                ['8', 'Golf With Your Friends', '2,300', '3,337'],
                                ['9', 'Jackbox Party Packs', '2,012', '4,211'],
                                ['10', 'Party Animals', '1,981', '3,240'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Stickiness is defined as the number of streams divided by unique streamers and represents how dedicated a community is to a particular game. '),
                            em('Musical Vibes'),
                            text(' was the April leader because nearly 80% of the stream count was from one music creator who misclassified their music stream as the party game category. The real king of stickiness continues to be '),
                            em('Words On Stream'),
                            text(', which finally surpassed '),
                            em('Ragnarok X: Next Generation'),
                            text(' in the rankings. Honorable mention goes to '),
                            em('nanos world'),
                            text(' which had a stellar month with their Zenkai server and the dedicated French streaming community.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'Musical Vibes', '11.8', '236', '20'],
                                ['2', 'Words On Stream', '11.1', '7,215', '648'],
                                ['3', 'Gartic on Stream', '10.9', '459', '42'],
                                ['4', 'nanos world', '10.6', '2,127', '201'],
                                ['5', 'Ragnarok X: Next Generation', '9.1', '518', '57'],
                                ['6', 'Dragon Warrior IV', '7.5', '241', '32'],
                                ['7', 'Mafia Online', '6.4', '167', '26'],
                                ['8', 'Dragon Ball: The Breakers', '6.0', '626', '105'],
                                [
                                    '9',
                                    'DEAD OR ALIVE Xtreme Venus Vacation',
                                    '6.0',
                                    '173',
                                    '29',
                                ],
                                [
                                    '10',
                                    'Placid Plastic Duck Simulator',
                                    '5.9',
                                    '1,827',
                                    '311',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            blocks: [
                p("April's new-release slate of 37 games produced one clear winner and a long tail of games we haven't observed yet on stream."),
            ],
            sections: [
                section({
                    id: 'new-releases__new-releases-seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Flock Around'),
                            text(' gained immediate momentum with hundreds of streamers giving it a shot in its first week after release. '),
                            em("Don't Lose Aggro"),
                            text(' had much less activity, but a very high average viewership thanks to streamers like '),
                            link('https://twitch.tv/sasatikk', 'sasatikk'),
                            text(' who brought more than 5,000 viewers to the title. '),
                            em('2 Cooks 1 Mess'),
                            text(' was observed many times on Twitch, primarily on a channel associated with its developer.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['Flock Around', 'Apr 24', '324', '282', '30'],
                                ['2 Cooks 1 Mess', 'Apr 15', '206', '5', '1'],
                                ['Nullpoint Protocol', 'Apr 6', '36', '22', '7'],
                                ["Don't Lose Aggro", 'Apr 15', '33', '31', '184'],
                                ['Cool Story Bro!', 'Apr 10', '14', '14', '15'],
                                ['Fruit Mountain Party', 'Apr 23', '1', '1', '36'],
                                ['Kaporanga', 'Apr 14', '1', '1', '1'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('There were 30 other party games launched in April without observed Twitch activity. We list them below to bring awareness to these new games and help streamers discover something new.'),
                        list({
                            items: [
                                'Fling Up Together (Apr 1)',
                                'Cat & Friends: Purrfect Jump (Apr 3)',
                                'Cat Doctor: For Loyal Servants (Apr 9)',
                                'Gentle Classical Music Course for Grown-Ups (Apr 9)',
                                'National Adult General Knowledge Selection Test (Apr 9)',
                                'Science Literacy Check for Adults (Apr 9)',
                                'Casual Sport Series: Racket Sports Bundle (Apr 10)',
                                'Can You Explain It? World History Test (Apr 16)',
                                'Hyakki Fantasia Mini Game Selection Vol.1 (Apr 16)',
                                'Japanese Culture Literacy Test (Apr 16)',
                                'Make it! Gyoza (Apr 16)',
                                'Nationwide Academic Check Junior High Level (Apr 16)',
                                "Skidddddd: Tori's Frozen Racing (Apr 17)",
                                'Amazing Facts World Culture True or False Quiz (Apr 23)',
                                'Amazing Lab! Science Experiments Quiz (Apr 23)',
                                'Card&Casino (Apr 23)',
                                'Dark Secrets Mystery Files (Apr 23)',
                                'Learn with Illustrations! English Prepositions 4-Choice Quiz (Apr 23)',
                                'Locked-Room Mystery: Hidden Truths in Everyday Life (Apr 23)',
                                'Party Pool (Apr 23)',
                                'Climber Toys: Together (Apr 24)',
                                'Gorilla Showdown (Apr 24)',
                                'Indoor Baseball (Apr 24)',
                                'Order of the Ivy (Apr 24)',
                                "Octo's Balloon Challenge (Apr 26)",
                                "Pirarucu's Money Rush (Apr 26)",
                                "Snail's Knock Out! (Apr 26)",
                                "Toad's Soul Hopper (Apr 26)",
                                'You Got A Problem?! (Apr 27)',
                                'Off the Beaten Track (Apr 29)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            blocks: [
                p('Consistency, viewership, and variety: these are the things that define a great streamer. Here we spotlight the creators who caught our attention in April.'),
            ],
            sections: [
                section({
                    id: 'streamer-spotlight__streamers-with-most-activity',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('The most active list is entirely comprised of single-game channels. Five of the top 10 are playing the same game, '),
                            em('Words On Stream'),
                            text(". Six of the top 10 also sat in March's list, so we primarily see the same always-on feeds resurfacing rather than a rotating cast of variety hosts."),
                        ]),
                        p([
                            link('https://twitch.tv/telinha', 'Telinha'),
                            text(' is an outlier where extreme capture volume on '),
                            em('Fall Guys'),
                            text(' still pairs with impressive triple-digit average viewers.'),
                        ]),
                        p([
                            text('Among repeat names, '),
                            link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                            text(' ('),
                            em('Super Smash Bros. Ultimate'),
                            text(') saw average viewers nearly double from prior months, while viewership of '),
                            link('https://twitch.tv/mrllamavod', 'mrllamavod'),
                            text(' ('),
                            em('Diablo II: Resurrected'),
                            text(') has continued to cool off month-over-month, following the broader D2R streaming trends.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '204',
                                    '24',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '203',
                                    '33',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '201',
                                    '90',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '200',
                                    '213',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/tecnowords', 'TecnoWords')],
                                    '199',
                                    '10',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '193',
                                    '21',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                    '189',
                                    '116',
                                    '1',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/cabgxaero', 'CABGxAero')],
                                    '179',
                                    '10',
                                    '1',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/xgeera', 'xGeera')],
                                    '151',
                                    '89',
                                    '1',
                                ],
                                [
                                    '10',
                                    [
                                        link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                    ],
                                    '145',
                                    '10',
                                    '1',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 captures',
                    collapsible: true,
                    blocks: [
                        p([
                            text('For the third month in a row '),
                            link('https://twitch.tv/caseoh_', 'caseoh_'),
                            text(' has led the party game Streamers with Biggest Audiences. Streamers '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' and '),
                            link('https://twitch.tv/Northernlion', 'Northernlion'),
                            text(' also hit the top 10 back-to-back-to-back. Spanish-language streamer '),
                            link('https://twitch.tv/loud_coringa', 'loud_coringa'),
                            text(' enters the top 5 this month with popular streams of '),
                            em('Make Way'),
                            text(' and '),
                            em('Super Battle Golf'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Captures',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                    '52,885',
                                    '3',
                                    '2',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '36,148',
                                    '7',
                                    '3',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                    '26,354',
                                    '2',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/northernlion', 'Northernlion')],
                                    '13,140',
                                    '3',
                                    '3',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/loud_coringa', 'loud_coringa')],
                                    '11,351',
                                    '3',
                                    '2',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/gon_vl', 'gon_vl')],
                                    '10,988',
                                    '2',
                                    '2',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/zerator', 'ZeratoR')],
                                    '9,254',
                                    '3',
                                    '2',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/gotaga', 'Gotaga')],
                                    '8,645',
                                    '2',
                                    '2',
                                ],
                                [
                                    '9',
                                    [
                                        link('https://twitch.tv/joueur_du_grenier', 'Joueur_du_Grenier'),
                                    ],
                                    '7,534',
                                    '2',
                                    '2',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/rdcgaming', 'RDCgaming')],
                                    '7,436',
                                    '4',
                                    '3',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-most-versatility',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/partychads', 'PARTYCHADS'),
                            text(' ranked first on game breadth in April, with captures leaning into the classic '),
                            em('Mario Party'),
                            text(' catalog entries and other retro party games. '),
                            link('https://twitch.tv/pietsmiettv', 'PietSmietTV'),
                            text(' moved up the ranks with consistent triple-digit viewership playing games like '),
                            em('UNO'),
                            text('. Also spotted playing 10+ games this month were '),
                            link('https://twitch.tv/super_cubby', 'super_cubby'),
                            text(' and '),
                            link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY'),
                            text(', with the latter reaching 1,500 viewers on infrequently-streamed games like '),
                            em('Pixoguess'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Captures',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '13',
                                    '78',
                                    '21',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '12',
                                    '16',
                                    '206',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/super_cubby', 'super_cubby')],
                                    '10',
                                    '19',
                                    '54',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY')],
                                    '10',
                                    '14',
                                    '1,264',
                                ],
                                [
                                    '5',
                                    [
                                        link('https://twitch.tv/sidearms4reason', 'SideArms4Reason'),
                                    ],
                                    '9',
                                    '12',
                                    '144',
                                ],
                                [
                                    '6',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '8',
                                    '33',
                                    '86',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/blackholebumm', 'blackholebumm')],
                                    '8',
                                    '24',
                                    '22',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/quarrysea', 'QuarrySea')],
                                    '8',
                                    '19',
                                    '50',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/dafmanhd', 'DafmanHD')],
                                    '8',
                                    '13',
                                    '23',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/scratchysn', 'ScratchYSN')],
                                    '8',
                                    '10',
                                    '29',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            blocks: [
                p('Party game streams spanned the same 35 languages in April as they did in March.'),
            ],
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('While English continues to dominate, French streaming surged in April and gained enough volume to pass German in the rankings. While there were more French streamers than last month, the real story is the increased dedication to streaming games like '),
                            em('nanos world'),
                            text(' and the strong average viewership per stream compared to other languages. While still on a small scale, one of the largest relative increases has been in the number of Thai streamers from month-to-month (ranked #13 in April).'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '122,093', '49,726', '17'],
                                ['2', 'Spanish', '15,766', '7,648', '14'],
                                ['3', 'French', '11,152', '4,223', '38'],
                                ['4', 'German', '9,286', '4,390', '54'],
                                ['5', 'Japanese', '8,594', '3,420', '29'],
                                ['6', 'Russian', '8,128', '3,474', '22'],
                                ['7', 'Portuguese', '4,533', '2,213', '31'],
                                ['8', 'Chinese', '3,304', '1,441', '43'],
                                ['9', 'Italian', '1,206', '584', '26'],
                                ['10', 'Polish', '934', '477', '52'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__english',
                    title: 'English',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__english__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '10,615', '7,364'],
                                        ['2', 'Lethal Company', '10,138', '6,042'],
                                        ['3', 'Super Smash Bros. Ultimate', '8,402', '2,429'],
                                        ['4', 'Among Us', '7,763', '4,587'],
                                        ['5', 'Fall Guys', '7,582', '3,701'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', '8 Ball Pool', '3,925', '14'],
                                        ['2', '8POOL', '703', '72'],
                                        ['3', 'West Hunt', '621', '12'],
                                        ['4', 'Suika Game', '444', '32'],
                                        ['5', 'Casino', '399', '31'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '204',
                                            '24',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '203',
                                            '33',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '201',
                                            '90',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '201',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tecnowords', 'TecnoWords')],
                                            '199',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                            '52,885',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/northernlion', 'Northernlion'),
                                            ],
                                            '13,140',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/rdcgaming', 'RDCgaming')],
                                            '7,436',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/maximilian_dood', 'Maximilian_DOOD'),
                                            ],
                                            '6,081',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/zentreya', 'Zentreya')],
                                            '4,274',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__spanish',
                    title: 'Spanish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__spanish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,862', '1,107'],
                                        ['2', 'Fall Guys', '1,862', '1,029'],
                                        ['3', 'Super Smash Bros. Ultimate', '1,696', '575'],
                                        ['4', 'Lethal Company', '1,011', '674'],
                                        ['5', 'Mario Kart World', '896', '329'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'LEGO Party!', '722', '7'],
                                        ['2', 'Kinect Sports', '701', '5'],
                                        ['3', 'Wii Sports', '195', '20'],
                                        ['4', 'PlateUp!', '132', '46'],
                                        ['5', 'Spider Roulette', '122', '6'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/xgeera', 'xGeera')],
                                            '151',
                                            '89',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sliverrk', 'SliverRK')],
                                            '74',
                                            '258',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/ravenslgaming', 'ravenslgaming'),
                                            ],
                                            '31',
                                            '13',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/pibe_lgsuz', 'pibe_lgsuz')],
                                            '30',
                                            '18',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/x_kotre', 'x_kotre')],
                                            '29',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/agustinunaplay8', 'AgustinUnaplay8'),
                                            ],
                                            '4,438',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,386',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/elcamacho24', 'elcamacho24')],
                                            '1,746',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/elded', 'elded')],
                                            '1,712',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '1,186',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__french',
                    title: 'French',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__french__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'nanos world', '2,040', '196'],
                                        ['2', 'Among Us', '1,046', '624'],
                                        ['3', 'Fall Guys', '975', '397'],
                                        ['4', 'Mario Kart World', '872', '324'],
                                        ['5', 'Super Smash Bros. Ultimate', '867', '269'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'The House Always Wins', '582', '8'],
                                        ['2', "Liar's Bar", '498', '63'],
                                        ['3', 'Maitre des Fleurs', '441', '33'],
                                        ['4', 'PICO PARK 2', '350', '25'],
                                        ['5', 'Pummel Party', '311', '39'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/anas6424v1', 'anas6424v1')],
                                            '201',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/walidoen17', 'walidoen17')],
                                            '55',
                                            '45',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ospride', 'OsPride')],
                                            '54',
                                            '26',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/queenihtv', 'QueenihTV')],
                                            '50',
                                            '45',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/wamoun', 'Wamoun')],
                                            '48',
                                            '166',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/zerator', 'ZeratoR')],
                                            '9,254',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/gotaga', 'Gotaga')],
                                            '8,645',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/joueur_du_grenier', 'Joueur_du_Grenier'),
                                            ],
                                            '7,534',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '3,730',
                                            '10',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/etoiles', 'Etoiles')],
                                            '3,117',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__german',
                    title: 'German',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__german__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,712', '937'],
                                        ['2', 'Fall Guys', '813', '402'],
                                        ['3', 'Mario Kart 8 Deluxe', '681', '303'],
                                        ['4', 'Golf With Your Friends', '659', '414'],
                                        ['5', 'Diablo II: Resurrected', '496', '107'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', "Liar's Bar", '449', '83'],
                                        ['2', 'Make Way', '234', '19'],
                                        ['3', 'Whatsamusic', '177', '6'],
                                        ['4', 'Among Us', '155', '1,712'],
                                        ['5', 'Golf With Your Friends', '133', '659'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/toom4s', 'toom4s')],
                                            '53',
                                            '28',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/allesinbuddha', 'AllesInBuddha'),
                                            ],
                                            '34',
                                            '13',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                            ],
                                            '33',
                                            '86',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/meloi_tv', 'Meloi_TV')],
                                            '31',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vl1x', 'vl1x')],
                                            '28',
                                            '24',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '36,148',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/krokoboss', 'krokoboss')],
                                            '5,280',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/xthesolutiontv', 'xTheSolutionTV'),
                                            ],
                                            '4,363',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mertabimula', 'mertabimula')],
                                            '4,298',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/coachlim', 'coachlim')],
                                            '2,802',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__japanese',
                    title: 'Japanese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__japanese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '2,009', '746'],
                                        ['2', 'Super Smash Bros. Ultimate', '1,027', '251'],
                                        ['3', 'Beat Saber', '728', '161'],
                                        ['4', 'Among Us', '407', '321'],
                                        ['5', 'Mario Kart World', '378', '125'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        [
                                            '1',
                                            'Super Mario Party Jamboree: Nintendo Switch 2 Edition + Jamboree TV',
                                            '1,347',
                                            '6',
                                        ],
                                        ['2', "Don't Lose Aggro", '841', '6'],
                                        ['3', 'Goose Goose Duck', '683', '117'],
                                        [
                                            '4',
                                            'Clubhouse Games: 51 Worldwide Classics',
                                            '309',
                                            '42',
                                        ],
                                        ['5', 'Super Mario Party Jamboree', '184', '17'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/pokapokacan', 'ぽかぽかさん')],
                                            '46',
                                            '32',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sabomkw', 'さっぼさん')],
                                            '39',
                                            '14',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nohoho12', 'のほほ')],
                                            '36',
                                            '240',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/knobara', '釘崎くぎみん')],
                                            '36',
                                            '49',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kai_shimotsuki', '霜月珈唯')],
                                            '36',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                            '26,354',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/gon_vl', 'gon_vl')],
                                            '10,988',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/peintooon', 'ぺいんと')],
                                            '5,052',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tototmix', 'ととみっくす')],
                                            '3,476',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/akamikarubi', '赤見かるび')],
                                            '3,453',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__russian',
                    title: 'Russian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__russian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Lethal Company', '1,333', '622'],
                                        ['2', 'Gartic Phone', '1,318', '606'],
                                        ['3', 'Jackbox Party Packs', '791', '252'],
                                        ['4', 'Diablo II: Resurrected', '620', '117'],
                                        ['5', 'SIGame', '446', '313'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Casino', '747', '18'],
                                        ['2', 'Super Crazy Rhythm Castle', '278', '6'],
                                        ['3', 'Monopoly Plus', '260', '7'],
                                        ['4', 'Octodad: Dadliest Catch', '103', '10'],
                                        ['5', 'Goose Goose Duck', '69', '396'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '189',
                                            '116',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kos_jv', 'Kos_JV')],
                                            '49',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vangavgav', 'vangavgav')],
                                            '41',
                                            '24',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/sashatechnic', 'SashaTechnic'),
                                            ],
                                            '37',
                                            '20',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/p5gd1pro', 'P5GD1pro')],
                                            '34',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/orkpod', 'orkpod')],
                                            '4,146',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sleduck', 'SleDuck')],
                                            '3,534',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/c_a_k_e', 'C_a_k_e')],
                                            '2,693',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/saylowzol', 'SAYLOWZOL')],
                                            '1,811',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/dmitry_bale', 'DMITRY_BALE')],
                                            '1,659',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__portuguese',
                    title: 'Portuguese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__portuguese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '505', '172'],
                                        ['2', 'Lethal Company', '349', '233'],
                                        ['3', 'Gartic Phone', '247', '143'],
                                        ['4', 'Words On Stream', '245', '51'],
                                        ['5', 'Stumble Guys', '238', '95'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Make Way', '2,665', '5'],
                                        ['2', 'Legend of Legaia', '212', '6'],
                                        ['3', 'MicroWorks', '180', '8'],
                                        ['4', 'Super Battle Golf', '117', '215'],
                                        ['5', 'Fall Guys', '92', '505'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '200',
                                            '213',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/boozeiira', 'Boozeiira')],
                                            '111',
                                            '28',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/miconight', 'MICOnight')],
                                            '47',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tiowillshow', 'TioWillShow')],
                                            '38',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/nasanavox', 'NasaNavox')],
                                            '35',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/loud_coringa', 'loud_coringa'),
                                            ],
                                            '11,351',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/felps', 'Felps')],
                                            '728',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/smzinho', 'Smzinho')],
                                            '704',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/gaules', 'Gaules')],
                                            '657',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/saullo', 'Saullo')],
                                            '558',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__chinese',
                    title: 'Chinese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__chinese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '1,106', '452'],
                                        ['2', 'DisasterPR+', '243', '216'],
                                        ['3', 'Goose Goose Duck', '239', '164'],
                                        ['4', 'Super Battle Golf', '238', '207'],
                                        ['5', 'Mabinogi', '181', '40'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Miitopia', '2,049', '6'],
                                        ['2', 'Jinsei Game for Nintendo Switch', '1,287', '11'],
                                        ['3', "Liar's Bar", '424', '18'],
                                        ['4', 'Among Us', '193', '45'],
                                        ['5', 'Lethal Company', '171', '83'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/news86', 'News86')],
                                            '28',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/11ee11', '伊伊是伊伊')],
                                            '17',
                                            '13',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/youngcatail', '脆仔')],
                                            '16',
                                            '20',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/miriri_14me', '咪咪哩')],
                                            '16',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/aster_0102', '艾絲特薾')],
                                            '15',
                                            '42',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/kr1stw', '西南69')],
                                            '4,360',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/hennie2001', '烟花蹦蹦蹦')],
                                            '2,977',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/paroniie', '帕蘿妮')],
                                            '2,430',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/p1lepe1el1', '珮蕾')],
                                            '2,068',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/reirei_neon', '澪れい_霓')],
                                            '2,049',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__italian',
                    title: 'Italian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__italian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '212', '94'],
                                        ['2', 'Among Us', '179', '83'],
                                        ['3', 'Mario Kart World', '69', '25'],
                                        ['4', 'Super Smash Bros. Ultimate', '62', '24'],
                                        ['5', 'Gartic Phone', '53', '44'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '310', '43'],
                                        ['2', 'Super Bunny Man', '103', '8'],
                                        ['3', 'Live Bingo', '90', '5'],
                                        ['4', 'Just Dance 2026 Edition', '52', '19'],
                                        ['5', 'Golf With Your Friends', '45', '18'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/baldonetv', 'BaldoneTV')],
                                            '31',
                                            '92',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/nonnogamer70', 'NonnoGamer70'),
                                            ],
                                            '25',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '19',
                                            '24',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dafmanhd', 'DafmanHD')],
                                            '13',
                                            '23',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lexandzr', 'lexandzr')],
                                            '13',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/manuuxo', 'ManuuXO')],
                                            '3,910',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/leutum', 'LEUTUM')],
                                            '897',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/itermosifoni', 'iTermosifoni'),
                                            ],
                                            '386',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/andreadel1988', 'andreadel1988'),
                                            ],
                                            '342',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '127',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__polish',
                    title: 'Polish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__polish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '233', '49'],
                                        ['2', 'Among Us', '102', '62'],
                                        ['3', 'Fall Guys', '98', '31'],
                                        ['4', 'Lethal Company', '87', '62'],
                                        ['5', 'Gartic Phone', '51', '41'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Among Us', '356', '102'],
                                        ['2', 'Super Battle Golf', '52', '9'],
                                        ['3', 'Keep Talking and Nobody Explodes', '43', '7'],
                                        ['4', 'Fall Guys', '39', '98'],
                                        ['5', 'Gartic Phone', '25', '51'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/lucy_sally', 'Lucy_Sally')],
                                            '29',
                                            '31',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/nadinwins', 'NadinWins')],
                                            '18',
                                            '117',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/eybiak', 'Eybiak')],
                                            '6',
                                            '34',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/karmazynowyy_', 'karmazynowyy_'),
                                            ],
                                            '6',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kalonxeno', 'KalonXeno')],
                                            '6',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/nexe_', 'Nexe_')],
                                            '5,135',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bladii309', 'Bladii309')],
                                            '296',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/julcziana', 'julcziana')],
                                            '166',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/wpatka', 'WPATKA')],
                                            '142',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/zwierzaaak', 'ZWIERZAAAK')],
                                            '138',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            blocks: [
                p([
                    text('The following tables dive deep into the top 10 games by Party Score and reveal how they performed so well in April, as well as the streamers behind their success. Newcomer to the list, '),
                    em('Placid Plastic Duck Simulator'),
                    text(', was buoyed by streamers like '),
                    link('https://twitch.tv/katha', 'katha'),
                    text(' who regularly incorporated the game into her subathon. '),
                    em('Pokémon Stadium'),
                    text(' was also a fresh addition to the top 10, largely thanks to '),
                    link('https://twitch.tv/Ray', 'Ray'),
                    text(' who brought his massive audience multiple times to the title.'),
                ]),
            ],
            sections: [
                section({
                    id: 'game-deep-dives__nanos-world',
                    title: 'nanos world',
                    collapsible: true,
                    blocks: [
                        p('2,127 streams | 201 streamers | 10.6 stickiness | 65 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/walidoen17', 'walidoen17')],
                                            '55',
                                            '45',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ospride', 'OsPride')],
                                            '54',
                                            '26',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/queenihtv', 'QueenihTV')],
                                            '50',
                                            '45',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/wamoun', 'Wamoun')],
                                            '48',
                                            '166',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '41',
                                            '950',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/qassimiento', 'Qassimiento')],
                                            '1,264',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '950',
                                            '41',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/hamzakerdaloco', 'HamzaKerdaloco'),
                                            ],
                                            '591',
                                            '21',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tayoh_', 'Tayoh_')],
                                            '523',
                                            '22',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/betr0uni', 'Betr0uni')],
                                            '306',
                                            '37',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('7,215 streams | 648 streamers | 11.1 stickiness | 17 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '203',
                                            '33',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '201',
                                            '9',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tecnowords', 'TecnoWords')],
                                            '199',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '193',
                                            '21',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/cabgxaero', 'CABGxAero')],
                                            '179',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/piapiufo', 'PiaPiUFO')],
                                            '451',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/buni', 'Buni')],
                                            '344',
                                            '37',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/atomikgamez', 'AtomikGamez')],
                                            '244',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/skinnedteen', 'skinnedteen')],
                                            '239',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/emilycc', 'Emilycc')],
                                            '230',
                                            '98',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-world',
                    title: 'Mario Kart World',
                    collapsible: true,
                    blocks: [
                        p('6,038 streams | 2,472 streamers | 2.4 stickiness | 25 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sliverrk', 'SliverRK')],
                                            '68',
                                            '262',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vantox_', 'Vantox_')],
                                            '53',
                                            '66',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/greezyszn', 'Greezyszn')],
                                            '26',
                                            '12',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/shy_guay', 'shy_guay')],
                                            '25',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/elviti', 'elviti')],
                                            '24',
                                            '18',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '1,300',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/gamescage', 'GamesCage')],
                                            '438',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/squirrel147', 'SQuirrel147')],
                                            '374',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kolderiu', 'Kolderiu')],
                                            '351',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/paulinpelivideot', 'PaulinPelivideot'),
                                            ],
                                            '336',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('8,983 streams | 1,568 streamers | 5.7 stickiness | 16 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '201',
                                            '90',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/alexislegit', 'AlexIsLegit')],
                                            '131',
                                            '13',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tony_awp', 'tony_awp')],
                                            '74',
                                            '25',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/actually_coley', 'Actually_Coley'),
                                            ],
                                            '70',
                                            '18',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/generalbardock', 'GeneralBardock'),
                                            ],
                                            '67',
                                            '20',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/alderiate', 'Alderiate')],
                                            '1,036',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/avoidingthepuddle', 'AvoidingThePuddle'),
                                            ],
                                            '960',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '694',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/barricade', 'Barricade')],
                                            '340',
                                            '21',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/emilycc', 'Emilycc')],
                                            '326',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-64',
                    title: 'Mario Kart 64',
                    collapsible: true,
                    blocks: [
                        p('326 streams | 118 streamers | 2.8 stickiness | 55 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mario-kart-64__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/abney317', 'abney317')],
                                            '25',
                                            '344',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mk64mr', 'MK64MR')],
                                            '17',
                                            '58',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/grey0__', 'grey0__')],
                                            '17',
                                            '12',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/dansgame89alt', 'dansgame89alt'),
                                            ],
                                            '13',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lrxc_', 'LRXC_')],
                                            '8',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mario-kart-64__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/raysfire', 'raysfire')],
                                            '483',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/abney317', 'abney317')],
                                            '344',
                                            '25',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mk64mr', 'MK64MR')],
                                            '58',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/skapp_tv', 'Skapp_tv')],
                                            '29',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mophmeister', 'Mophmeister')],
                                            '16',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-smash-bros-ultimate',
                    title: 'Super Smash Bros. Ultimate',
                    collapsible: true,
                    blocks: [
                        p('12,666 streams | 3,761 streamers | 3.4 stickiness | 15 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '204',
                                            '24',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/afk_host', 'AFK_Host')],
                                            '195',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/amiibots', 'Amiibots')],
                                            '183',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/cirrentv', 'CirrenTV')],
                                            '46',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/metalvayne08', 'metalvayne08'),
                                            ],
                                            '39',
                                            '39',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sakor_', 'Sakor_')],
                                            '1,021',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vgbootcamp2', 'VGBootCamp2')],
                                            '968',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vgbootcamp', 'VGBootCamp')],
                                            '871',
                                            '34',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/hungrybox', 'Hungrybox')],
                                            '693',
                                            '15',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/momochoco', 'momochoco')],
                                            '520',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mahjong-soul',
                    title: 'Mahjong Soul',
                    collapsible: true,
                    blocks: [
                        p('4,229 streams | 1,611 streamers | 2.6 stickiness | 18 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xsinstream222', 'xsinstream222'),
                                            ],
                                            '46',
                                            '31',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/knobara', '釘崎くぎみん')],
                                            '36',
                                            '49',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/gomashiojijii', 'ごましおじじい'),
                                            ],
                                            '29',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/hika_tin115', 'ひかちゃんねる'),
                                            ],
                                            '27',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/takum1rmu', 'おかでぃーです')],
                                            '18',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ev913', 'Ev913')],
                                            '1,306',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/migi_tw', '米姬')],
                                            '1,087',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/earendelxdfp', '厄倫蒂兒')],
                                            '1,022',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kokujintv', 'kokujintv')],
                                            '960',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lovelymomo', 'lovelymomo')],
                                            '695',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__placid-plastic-duck-simulator',
                    title: 'Placid Plastic Duck Simulator',
                    collapsible: true,
                    blocks: [
                        p('1,827 streams | 311 streamers | 5.9 stickiness | 14 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__placid-plastic-duck-simulator__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/katha', 'katha')],
                                            '123',
                                            '24',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/soothingmusic24', 'Soothingmusic24'),
                                            ],
                                            '123',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/thetw1st3dj', 'TheTw1st3dJ')],
                                            '75',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/th3beardedbaron', 'th3beardedbaron'),
                                            ],
                                            '51',
                                            '140',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/skika', 'skika')],
                                            '50',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__placid-plastic-duck-simulator__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/moonbtch', 'MOONBTCH')],
                                            '224',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/djunreal', 'DJUnreal')],
                                            '150',
                                            '24',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/th3beardedbaron', 'th3beardedbaron'),
                                            ],
                                            '140',
                                            '51',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/nukadave', 'NukaDave')],
                                            '118',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/dropsy_', 'dropsy_')],
                                            '90',
                                            '13',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__just-dance-2026-edition',
                    title: 'Just Dance 2026 Edition',
                    collapsible: true,
                    blocks: [
                        p('495 streams | 214 streamers | 2.3 stickiness | 42 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/dellamaurie', 'dellAMAURIE')],
                                            '33',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ulises_eric', 'Ulises_Eric')],
                                            '15',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/sierravampire', 'SierraVampire'),
                                            ],
                                            '14',
                                            '67',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/darth_diez', 'Darth_Diez')],
                                            '13',
                                            '25',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/floridawoman', 'floridawoman'),
                                            ],
                                            '12',
                                            '32',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,348',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,289',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/motfrais', 'motfrais')],
                                            '244',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/sincerelylyn', 'sincerelyLyn'),
                                            ],
                                            '198',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sophelyy', 'Sophelyy')],
                                            '163',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__pok-mon-stadium',
                    title: 'Pokémon Stadium',
                    collapsible: true,
                    blocks: [
                        p('219 streams | 98 streamers | 2.2 stickiness | 87 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__pok-mon-stadium__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/werster247', 'werster247')],
                                            '12',
                                            '28',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/pixelstation64', 'PixelStation64'),
                                            ],
                                            '12',
                                            '26',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/werster', 'werster')],
                                            '10',
                                            '148',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/alissonsuriani', 'AlissonSuriani'),
                                            ],
                                            '7',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ray', 'Ray')],
                                            '5',
                                            '3,058',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__pok-mon-stadium__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ray', 'Ray')],
                                            '3,058',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/werster', 'werster')],
                                            '148',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/imglower', 'imglower')],
                                            '46',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/werster247', 'werster247')],
                                            '28',
                                            '12',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/pixelstation64', 'PixelStation64'),
                                            ],
                                            '26',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            blocks: [
                p('A remarkably similar pattern emerged between the April and March data, pointing towards predictable streaming patterns across time zones and days of the week.'),
            ],
            sections: [
                section({
                    id: 'streaming-patterns__streaming-patterns-by-day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p('Thursday was the strongest viewing day, while Saturday was the highest-volume day by average streams per capture. Every day of the week saw a drop in the average number of streams and viewers, except for Friday which ticked up in both month-over-month.'),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '1,146', '19'],
                                ['Monday', '829', '21'],
                                ['Tuesday', '810', '22'],
                                ['Wednesday', '798', '22'],
                                ['Thursday', '829', '32'],
                                ['Friday', '1,008', '22'],
                                ['Saturday', '1,235', '15'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__streaming-patterns-by-hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p('April keeps a US/EU-weighted day shape, but primetime attention concentrates earlier on the clock (19:00-21:00 UTC) and the 22:00-00:00 UTC window noticeably weakened versus March. Late night streams (03:00-04:00 UTC) lost a lot of activity month-over-month without a matching viewer surge.'),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,342', '22'],
                                ['01:00', '1,532', '17'],
                                ['02:00', '1,602', '13'],
                                ['03:00', '1,304', '23'],
                                ['04:00', '1,066', '20'],
                                ['05:00', '979', '18'],
                                ['06:00', '797', '13'],
                                ['07:00', '547', '14'],
                                ['08:00', '432', '18'],
                                ['09:00', '435', '15'],
                                ['10:00', '403', '17'],
                                ['11:00', '441', '24'],
                                ['12:00', '570', '18'],
                                ['13:00', '702', '20'],
                                ['14:00', '711', '21'],
                                ['15:00', '742', '23'],
                                ['16:00', '890', '18'],
                                ['17:00', '936', '25'],
                                ['18:00', '1,104', '24'],
                                ['19:00', '1,353', '31'],
                                ['20:00', '1,333', '35'],
                                ['21:00', '1,119', '36'],
                                ['22:00', '1,183', '21'],
                                ['23:00', '1,233', '20'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-04-top-party-games-and-streamers.js.map